import { useTracking } from '@repo/tracking/use-tracking';
import { Trans } from '@repo/translations/use-translation';
import { useState } from 'react';

import { useCartContext } from '@zooplus/cart-provider';
import { Anchor } from '@zooplus/ui-components';

import { useMembershipOffer } from '../../../../hooks/useMembershipOffer';
import { MembershipsComparison } from '../../../../hooks/useMembershipsComparison';
import { PremiumAddedModal } from '../../../UI/PremiumAddedModal/PremiumAddedModal';
import { ProgramPlanBenefits } from '../ProgramPlanBenefits/ProgramPlanBenefits';
import { ProgramPlanButtons } from '../ProgramPlanButtons/ProgramPlanButtons';
import { ProgramPlanHeader } from '../ProgramPlanHeader/ProgramPlanHeader';

import styles from './ProgramPlanContent.module.css';

export interface ProgramPlanContentProps {
  membershipsComparison: MembershipsComparison;
  isBasicCustomer: boolean;
  plan: 'BASIC' | 'PREMIUM';
}

export const ProgramPlanContent = ({
  membershipsComparison,
  isBasicCustomer,
  plan,
}: ProgramPlanContentProps) => {
  const isLoggedIn = !!membershipsComparison.customerLoyaltyLevelType;
  const { data: membership } = useMembershipOffer(isLoggedIn);
  const { cart, ready } = useCartContext();
  const { trackClick } = useTracking();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <div className={styles.plan}>
        <ProgramPlanHeader membershipsComparison={membershipsComparison} plan={plan} />
        <ProgramPlanBenefits plan={plan} membershipsComparison={membershipsComparison} />
        <div className={styles.buttonSection}>
          {ready ? (
            <ProgramPlanButtons
              isLoggedIn={isLoggedIn}
              plan={plan}
              isBasicCustomer={isBasicCustomer}
              hasOfferAvailable={membership !== undefined}
              articleId={membership?.offer.articleId}
              setIsModalOpen={setIsModalOpen}
            />
          ) : null}
          <span data-testid="terms-and-conditions" className={styles.termsAndConditions}>
            <Trans
              i18nKey={'loyalty-program.public-landing-page.program-plans.terms-and-conditions'}
              onClick={() => {
                trackClick({
                  target: {
                    name: 'terms_and_conditions',
                    id: 'terms_and_conditions_link',
                  },
                });
              }}
              components={{
                Link: (
                  <Anchor
                    className={styles.termsLink}
                    variant="neutral"
                    underline
                    isBold
                    href={
                      plan === 'PREMIUM'
                        ? '/info/legal/club_premium_terms'
                        : '/info/legal/club_basic_terms'
                    }
                  />
                ),
              }}
            />
          </span>
        </div>
      </div>
      <PremiumAddedModal
        data-testid={'add-membership-modal'}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        isCartEmpty={ready && !!cart.articles && cart.articles.length == 0}
      />
    </>
  );
};
